import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IssueSection, RANKS, ReportIssue, rankInfo } from '../../types/projectReport';
import { monthLabel } from '../../utils/projectReport';

/** One section's ranked issues: Safety items of concern, internal or external concerns. */

export const RankChip: React.FC<{ rank: number; size?: 'small' | 'medium' }> = ({ rank, size = 'small' }) => {
  const r = rankInfo(rank);
  if (!r) return <Chip size={size} label="Choose rank" variant="outlined" color="warning" />;
  return <Chip size={size} label={r.label} sx={{ bgcolor: r.color, color: r.text, fontWeight: 600 }} />;
};

const newKey = () => `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

interface Props {
  section: IssueSection;
  title: string;
  issues: ReportIssue[];
  onChange: (issues: ReportIssue[]) => void;
  month: string;
  disabled?: boolean;
  /** Keys of the rows with a problem the PM must fix before saving. */
  invalid?: Set<string>;
}

export const issueKey = (i: ReportIssue): string => i.key || `i${i.issueId}`;

const IssueList: React.FC<Props> = ({ section, title, issues, onChange, month, disabled, invalid }) => {
  const mine = issues.filter((i) => i.section === section);
  const replace = (target: ReportIssue, patch: Partial<ReportIssue> | null) =>
    onChange(issues.flatMap((i) => (i === target ? (patch ? [{ ...i, ...patch }] : []) : [i])));
  const add = () =>
    onChange([...issues, { key: newKey(), section, rank: 0, description: '', action: '', status: 'Open', firstPeriodMonth: month }]);

  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">{title}</Typography>
        {!disabled && <Button size="small" startIcon={<AddIcon />} onClick={add}>Add</Button>}
      </Stack>
      {mine.length === 0 && <Typography variant="body2" color="text.secondary">None reported.</Typography>}
      {mine.map((issue) => {
        const key = issueKey(issue);
        const bad = invalid?.has(key);
        const carried = issue.firstPeriodMonth && issue.firstPeriodMonth.slice(0, 7) < month.slice(0, 7);
        return (
          <Paper key={key} variant="outlined" sx={{ p: 1.5, borderColor: bad ? 'error.main' : undefined, borderLeft: `4px solid ${rankInfo(issue.rank)?.color || '#bdbdbd'}` }}>
            <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: '170px 1fr 1fr 130px auto' }, alignItems: 'start' }}>
              <FormControl size="small" fullWidth error={bad && !issue.rank}>
                <InputLabel id={`rank-${key}`}>Rank</InputLabel>
                <Select
                  labelId={`rank-${key}`}
                  label="Rank"
                  value={issue.rank || ''}
                  disabled={disabled}
                  onChange={(e) => replace(issue, { rank: Number(e.target.value) as ReportIssue['rank'] })}
                  renderValue={(v) => <RankChip rank={Number(v)} />}
                >
                  {RANKS.map((r) => (
                    <MenuItem key={r.rank} value={r.rank}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <RankChip rank={r.rank} />
                        <Typography variant="caption" color="text.secondary">{r.meaning}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Issue"
                size="small"
                multiline
                minRows={1}
                fullWidth
                value={issue.description}
                disabled={disabled}
                error={bad && !issue.description.trim()}
                onChange={(e) => replace(issue, { description: e.target.value })}
                inputProps={{ maxLength: 2000 }}
              />
              <TextField
                label="Action / support needed"
                size="small"
                multiline
                minRows={1}
                fullWidth
                value={issue.action}
                disabled={disabled}
                onChange={(e) => replace(issue, { action: e.target.value })}
                inputProps={{ maxLength: 2000 }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel id={`status-${key}`}>Status</InputLabel>
                <Select labelId={`status-${key}`} label="Status" value={issue.status} disabled={disabled} onChange={(e) => replace(issue, { status: e.target.value as ReportIssue['status'] })}>
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="Resolved">Resolved</MenuItem>
                </Select>
              </FormControl>
              {!disabled && (
                <Tooltip title="Remove">
                  <IconButton aria-label="Remove issue" onClick={() => replace(issue, null)} size="small"><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Tooltip>
              )}
            </Box>
            {carried && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Reported since {monthLabel(issue.firstPeriodMonth)}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default IssueList;
